import { type ReactNode } from "react";
import { useSearchParams } from "react-router";

import { KIT_SHEET_STAGES, KitSheetPreview } from "@/features/admin/households/kit-sheet/preview";
import {
  AirSensorsScreen,
  PART_A_STAGES,
  PART_B_STAGES,
  type Stage as AirStage,
} from "@/features/onboarding/air-sensors-screen";
import { MODULE_KEYS } from "@/features/onboarding/compartments";
import {
  FloorHeatingScreen,
  type Stage as HeatingStage,
  STAGES as HEATING_STAGES,
} from "@/features/onboarding/floor-heating-screen";
import { GreetingScreen } from "@/features/onboarding/greeting-screen";
import { HomeHubScreen } from "@/features/onboarding/home-hub-screen";
import {
  HubSetupScreen,
  type Stage as HubStage,
  STAGES as HUB_STAGES,
} from "@/features/onboarding/hub-setup-screen";
import {
  NetworkSetupScreen,
  type Stage as NetworkStage,
  STAGES as NETWORK_STAGES,
} from "@/features/onboarding/network-setup-screen";
import { OffboardingScreen } from "@/features/onboarding/offboarding-screen";
import { MODULE_NAMES, PRODUCT_NAMES } from "@/features/onboarding/product-names";
import {
  type Stage as VentStage,
  STAGES as VENT_STAGES,
  VentilationScreen,
} from "@/features/onboarding/ventilation-screen";
import {
  type Stage as WindowStage,
  STAGES as WINDOW_STAGES,
  WindowsScreen,
} from "@/features/onboarding/windows-screen";
import {
  type AirthingsSensorStatus,
  type GatewayDiagnosis,
  type HeatingZone,
  type OnboardingState,
  type Room,
  type WindowCover,
} from "@/lib/api";

/**
 * Dev-only screen gallery — every onboarding screen, at every stage, rendered
 * directly.
 *
 * Why this exists: in the real flow `FlowShell` picks the screen from the
 * household's persisted `onboarding_progress`, and several stages only advance
 * when live hardware answers (the hub poll, gateway provisioning, the AirThings
 * cloud, window discovery). Reviewing copy, images and screen splits through
 * that means resetting a real household and re-running physical hardware — so
 * design review used to cost a trip to the office kit.
 *
 * The screens themselves are self-contained: they hold their own stage state,
 * and `useAssistant()` / `useFlowNav()` both degrade to no-ops without their
 * providers. So they can be mounted straight from here with stub props.
 *
 * WHAT THIS IS NOT: a substitute for a real run. Stages that render live data
 * (hub version, discovered actuators, sensor serials, heating circuits) show
 * fallback or empty values here, and anything gated on a real response will sit
 * spinning — which is itself useful, since it's exactly what a user sees while
 * waiting. Verify behaviour against real hardware; use this for how it reads.
 *
 * Registered in `App.tsx` behind `import.meta.env.DEV`, so it never ships.
 *
 * URL: /preview?screen=<id>&stage=<stage>   (&chrome=0 hides the toolbar)
 */

/**
 * The stub house after an open-plan merge: the kitchen absorbed the living
 * room and keeps its own sensor; the living room stays on the record as
 * absorbed, carrying its sensor as retired, which is how merge_rooms stores
 * it. The closing task must list three rooms, not four, explain the merged
 * one, and offer the spare a home.
 */
function mergedRooms(rooms: Room[]): Room[] {
  const [kitchen, living, ...rest] = rooms;
  if (!kitchen || !living) {
    return rooms;
  }
  return [
    {
      ...kitchen,
      name: "Kitchen and Living room",
      ha_room_id: "kitchen_and_living_room",
      area_m2: (kitchen.area_m2 ?? 0) + (living.area_m2 ?? 0),
    },
    // The retired sensor stays on the absorbed room's record, as merge_rooms leaves it.
    {
      ...living,
      absorbed_into: "kitchen_and_living_room",
      devices: (living.devices ?? []).map((d) => ({ ...d, decommissioned: true })),
    },
    ...rest,
  ];
}

/** Stub house — four rooms, sensors and windows, mirroring a real scan. */
const ROOMS: Room[] = [
  {
    name: "Kitchen",
    area_m2: 14,
    devices: [
      { brand: "—", name: "Air sensor", device_type: "AirthingsSensor", external_id: "2960001234" },
    ],
    // Two identical roof windows, which is Esben's kitchen: same type, same
    // area, same orientation. Nothing in a scan can tell them apart, so this
    // is the case the rename and "move it" affordances exist for.
    windows: [
      { window_type: "roof", window_is_electric: true, has_blind: true },
      { window_type: "roof", window_is_electric: true, has_blind: true },
    ],
  },
  {
    name: "Living room",
    area_m2: 28,
    devices: [
      { brand: "—", name: "Air sensor", device_type: "AirthingsSensor", external_id: "2960005678" },
    ],
    windows: [
      { window_type: "roof", window_is_electric: true, has_blind: true },
      { window_type: "facade", window_is_electric: true, has_blind: false },
    ],
  },
  {
    name: "Master bedroom",
    area_m2: 16,
    devices: [
      { brand: "—", name: "Air sensor", device_type: "AirthingsSensor", external_id: "2960009012" },
    ],
    windows: [{ window_type: "roof", window_is_electric: true, has_blind: true }],
  },
  {
    name: "Home office",
    area_m2: 11,
    devices: [
      { brand: "—", name: "Air sensor", device_type: "AirthingsSensor", external_id: "2960003456" },
    ],
    windows: [{ window_type: "facade", window_is_electric: false, has_blind: false }],
  },
];

/**
 * Stand-in AirThings rows for the closing screen's victory view.
 *
 * Serials match ROOMS above, so every room tile finds its numbers. Without
 * these the victory view is unreachable here: it only renders once real
 * sensors report, and the gallery has no hardware and no session.
 */
const SENSOR_READINGS: AirthingsSensorStatus[] = [
  {
    serial_number: "2960001234",
    name: "Kitchen",
    responding: true,
    last_seen_minutes: 1,
    battery: 100,
    temp: 21.5,
    humidity: 42,
    co2: 468,
  },
  {
    serial_number: "2960005678",
    name: "Living room",
    responding: true,
    last_seen_minutes: 0,
    battery: 97,
    temp: 22.1,
    humidity: 45,
    co2: 512,
  },
  {
    serial_number: "2960009012",
    name: "Master bedroom",
    responding: true,
    last_seen_minutes: 3,
    battery: 92,
    temp: 19.8,
    humidity: 48,
    co2: 604,
  },
  {
    serial_number: "2960003456",
    name: "Home office",
    responding: true,
    last_seen_minutes: 2,
    battery: 88,
    temp: 20.6,
    humidity: 41,
    co2: 731,
  },
];

/**
 * Twelve circuits, the shape of Beta Test House 13, so the sheet can be
 * reviewed at the length that actually goes wrong. The gallery has no hub, and
 * a three-row list hides the thing this screen is judged on: what is still
 * within reach once the list is longer than the screen.
 */
const HEATING_CIRCUITS: HeatingZone[] = Array.from({ length: 12 }, (_, i) => ({
  zone: i + 1,
  climate_entity_id: `climate.wavin_ahc_9000_channel_${i + 1}`,
  circuits: [i + 1],
  current_temperature: 21 + (i % 4) * 0.5,
  target_temperature: 22,
  hvac_action: "idle",
  valve_open: false,
  thermostat_lost: false,
}));

/**
 * Stand-in actuators for the windows compartment's discovery and mapping.
 *
 * Five, matching the motorised products in ROOMS above, so the counts on
 * `discovering` and `safety` agree with the house scan. Without these, the
 * mapping loop finds nothing to open and drops straight to `done`, which is
 * correct in a real house with no windows paired and makes part three
 * impossible to review here.
 */
const WINDOW_COVERS: WindowCover[] = [
  { entity_id: "cover.velux_1", name: "Window 1", device_class: "window", position: 0 },
  { entity_id: "cover.velux_2", name: "Window 2", device_class: "window", position: 0 },
  { entity_id: "cover.velux_3", name: "Window 3", device_class: "window", position: 0 },
  { entity_id: "cover.velux_4", name: "Window 4", device_class: "window", position: 0 },
  { entity_id: "cover.velux_5", name: "Blind 1", device_class: "shade", position: 0 },
];

/**
 * The recap the closing screen shows: every compartment, named and ordered the
 * way `completedModuleNames` in flow-shell builds it, so reviewing the copy
 * here reviews the copy that ships.
 */
const COMPLETED_MODULE_NAMES = MODULE_KEYS.map((key) => MODULE_NAMES[key]!);

/**
 * The gallery's extra stage on `air-b`: the celebration the reading check
 * comes good into. Not a real `Stage` — the screen has no stage of its own for
 * it, it splits the `done` stage on whether the sensors answered — so the
 * gallery opens `done` and supplies the answer.
 */
const DONE_VICTORY = "done-victory";

/**
 * The gallery's three extra stages on the connector modules: the connecting
 * screen once the background diagnosis has landed a verdict.
 *
 * None is a real `Stage` — all three ARE `connecting`, they differ only in what
 * the diagnosis came back with — so the gallery opens `connecting` and supplies
 * the verdict. Three entries because the notices say different things to the
 * resident ("here is what to check", "this one is not yours to fix", and the
 * same again with one question worth answering anyway), and reproducing any of
 * them against real hardware means breaking a connector on purpose and waiting
 * for the hub to notice.
 */
const DIAGNOSIS_FIXABLE = "connecting-fixable";
const DIAGNOSIS_NEEDS_VELUX = "connecting-needs-velux";
const DIAGNOSIS_NEEDS_VELUX_QUESTION = "connecting-needs-velux-question";
const DIAGNOSIS_STAGES = [DIAGNOSIS_FIXABLE, DIAGNOSIS_NEEDS_VELUX, DIAGNOSIS_NEEDS_VELUX_QUESTION];

/**
 * The same extra stage on the hub screen, for a different reason.
 *
 * `DIAGNOSIS_NEEDS_VELUX` is a verdict on a connector; this one is a clock.
 * The hub screen has no diagnosis to stand in for: it turns on ten minutes
 * passing on `connecting` with nothing answering, which is not something a
 * review can sit through. It reads the same name because it is the same news
 * to the resident, minus the way on: nothing after the hub runs without it.
 */
const HUB_UNREACHABLE_STAGE = "connecting-needs-velux";

/**
 * Stand-in verdicts for those two stages, per gateway kind.
 *
 * The wording is lifted from `network_diagnosis.py` (the `no_data` and
 * `port_unreachable` branches), so what a designer reads here is what a
 * household reads. `no_data` is the on-site case that survived the escalation
 * pass: the hub reaches the connector and the unit behind it says nothing,
 * which is a cable in the wrong green socket and nobody's job but the person
 * standing at the cabinet. `port_unreachable` is the clearest of the
 * escalations, because the fault sits in a router nobody at VELUX can reach.
 *
 * `not_found` is not the fixable fixture any more. It escalates, and no verdict
 * asks a household to reconfigure a bridge, so the fixable side has to come
 * from a verdict that is still theirs to fix.
 *
 * The cable question names the connector by compartment, which is what the
 * backend falls back to when it has no MAC on record. A real house usually
 * gets the MAC-suffix form, and the gallery has no MAC to build one from.
 *
 * The escalate detail stops at the finding, with no promise of what happens
 * next, because the backend no longer writes one: the notice ends on a fixed
 * paragraph of its own (`ESCALATE_PROMISE`), so the promise is made once and
 * reads the same on every verdict that lands there.
 */
function diagnosisFixture(
  kind: GatewayDiagnosis["kind"],
  stage: string,
): GatewayDiagnosis | undefined {
  if (stage === DIAGNOSIS_FIXABLE) {
    const connector =
      kind === "wavin" ? PRODUCT_NAMES.heatingConnector : PRODUCT_NAMES.ventilationConnector;
    return {
      kind,
      verdict: "no_data",
      detail:
        "Your hub is reaching this connector, but the unit behind it is silent. Check the cable is pushed fully into the correct green socket on the connector. There are two, and only one is right.",
      action: "swap_cables",
      question: `Look at the ${connector}. Is the cable going into it yellow or blue? The two cables can be swapped by mistake, and putting them back is quick.`,
      needs_someone_on_site: true,
    };
  }
  if (stage === DIAGNOSIS_NEEDS_VELUX) {
    return {
      kind,
      verdict: "port_unreachable",
      detail:
        "Your hub can see this connector but cannot open a connection to it. That is usually a firewall, or smart devices being kept on a separate network.",
      action: "escalate",
      needs_someone_on_site: false,
    };
  }
  if (stage === DIAGNOSIS_NEEDS_VELUX_QUESTION) {
    // The same `not_found` fault as the fixable stage above, once the app
    // stopped pretending a household could be walked through a bridge
    // reconfiguration. It escalates, and it still asks its question: the
    // access-point name is the one piece of evidence only somebody standing in
    // the house can read, and it is what tells VELUX which fix to bring.
    return {
      kind,
      verdict: "not_found",
      detail: "Your hub cannot find this connector on your network.",
      action: "escalate",
      question:
        "Stand next to the connector and open the Wi-Fi list on your phone. Do you see a network whose name begins with USR-W610? If you do, tell us the four characters that come after the underscore.",
      needs_someone_on_site: false,
    };
  }
  return undefined;
}

/** Module order matches MODULE_KEYS — the physical kit box, compartments 1–5. */
const MODULE_STEPS: { key: string; title: string }[] = [
  { key: "home_assistant", title: "Configure Smart Hub" },
  { key: "air_sensors", title: "Air Quality" },
  { key: "windows", title: "Windows" },
  { key: "floor_heating", title: "Floor heating" },
  { key: "ventilation", title: "Ventilation" },
];

/**
 * A guide-screen state with the first `doneCount` modules completed.
 *
 * `skipped` marks those positions as skipped instead: VLX-76's whole finding
 * is that a skipped module looked completed, so the two states have to be
 * reviewable side by side.
 */
function guideState(doneCount: number, skipped: number[] = []): OnboardingState {
  return {
    household_name: "Verhoeven",
    household_status: "onboarding",
    first_name: "Francis",
    rooms: ROOMS,
    steps: MODULE_STEPS.map((step, index) => ({
      ...step,
      status: skipped.includes(index) ? "skipped" : index < doneCount ? "completed" : "pending",
    })),
    current_step_key: MODULE_STEPS[doneCount]?.key ?? null,
  };
}

const noop = () => undefined;

interface PreviewEntry {
  id: string;
  /** Kit compartment this screen belongs to, or "—" for shared screens. */
  compartment: string;
  label: string;
  stages: readonly string[];
  render: (stage: string) => ReactNode;
}

const SCREENS: PreviewEntry[] = [
  {
    id: "greeting",
    compartment: "—",
    label: "Greeting",
    stages: ["default"],
    render: () => <GreetingScreen firstName="Francis" busy={false} error="" onContinue={noop} />,
  },
  {
    id: "guide",
    compartment: "—",
    label: "Guide / Home Hub",
    // `all-done` vs `all-done-sensors`: the closing row reads differently once
    // the background poll has confirmed the sensors are live, and that state is
    // otherwise unreviewable here — it depends on an AirThings cloud answer.
    stages: ["fresh", "2-done", "all-done", "all-done-sensors", "skipped"],
    render: (stage) => (
      <HomeHubScreen
        state={guideState(
          stage === "fresh" ? 0 : stage === "2-done" ? 2 : 5,
          stage === "skipped" ? [3] : [],
        )}
        busy={false}
        error=""
        onOpenModule={noop}
        onOpenClosingTask={noop}
        sensorsReporting={stage === "all-done-sensors"}
        onFinish={noop}
      />
    ),
  },
  {
    id: "network",
    compartment: "1",
    label: "Hub · network prep",
    stages: NETWORK_STAGES,
    render: (stage) => (
      <NetworkSetupScreen
        initialStage={stage as NetworkStage}
        busy={false}
        error=""
        onComplete={noop}
      />
    ),
  },
  {
    id: "hub",
    compartment: "1",
    label: "Hub · connect",
    stages: [...HUB_STAGES, HUB_UNREACHABLE_STAGE],
    render: (stage) => {
      const unreachable = stage === HUB_UNREACHABLE_STAGE;
      return (
        <HubSetupScreen
          initialStage={unreachable ? "connecting" : (stage as HubStage)}
          busy={false}
          error=""
          onComplete={noop}
          // Forces the state and files nothing: the screen records its issue
          // as urgent, which sends a report the moment it happens, and a
          // design review must not do that to a real household.
          previewUnreachable={unreachable}
        />
      );
    },
  },
  {
    id: "air",
    compartment: "2",
    label: "Air Quality · part A",
    stages: PART_A_STAGES,
    render: (stage) => (
      <AirSensorsScreen
        part="a"
        initialStage={stage as AirStage}
        rooms={ROOMS}
        busy={false}
        error=""
        onComplete={noop}
      />
    ),
  },
  {
    id: "air-b",
    compartment: "—",
    // `done` is the reading check still waiting; `done-victory` is the same
    // stage once every placed sensor has answered. Two entries because they
    // are two screens to review, and only one of them is reachable here
    // without stand-in readings.
    label: "Closing task · place sensors",
    stages: [...PART_B_STAGES, DONE_VICTORY],
    render: (stage) => {
      const victory = stage === DONE_VICTORY;
      return (
        <AirSensorsScreen
          part="b"
          initialStage={victory ? "done" : (stage as AirStage)}
          rooms={victory ? ROOMS : mergedRooms(ROOMS)}
          busy={false}
          error=""
          onComplete={noop}
          previewReadings={victory ? SENSOR_READINGS : undefined}
          // Heating moves from the completed list to the skipped one, so the
          // yellow row is reviewable beside the green ones and no module is
          // claimed twice (VLX-76).
          completedSteps={
            victory
              ? COMPLETED_MODULE_NAMES.filter((name) => name !== MODULE_NAMES.floor_heating)
              : undefined
          }
          skippedSteps={victory ? [MODULE_NAMES.floor_heating!] : undefined}
        />
      );
    },
  },
  {
    id: "windows",
    compartment: "3",
    label: "VELUX Windows",
    stages: WINDOW_STAGES,
    render: (stage) => (
      <WindowsScreen
        initialStage={stage as WindowStage}
        rooms={ROOMS}
        busy={false}
        error=""
        onComplete={noop}
        previewCovers={WINDOW_COVERS}
        skipping={false}
        onSkip={noop}
      />
    ),
  },
  {
    id: "heating",
    compartment: "4",
    label: "Heating",
    stages: [...HEATING_STAGES, ...DIAGNOSIS_STAGES],
    render: (stage) => {
      const previewDiagnosis = diagnosisFixture("wavin", stage);
      return (
        <FloorHeatingScreen
          initialStage={previewDiagnosis ? "connecting" : (stage as HeatingStage)}
          rooms={ROOMS}
          previewCircuits={HEATING_CIRCUITS}
          busy={false}
          error=""
          onComplete={noop}
          skipping={false}
          onSkip={noop}
          previewDiagnosis={previewDiagnosis}
          // The confirm screen needs a shared thermostat; the gallery has no
          // hub, so it gets Esben's kitchen: one thermostat, kitchen and living room.
          previewSharedThermostats={
            stage === "confirm-merge"
              ? [{ zone: 5, circuits: [5, 6, 11, 12], rooms: ["Kitchen", "Living room"] }]
              : undefined
          }
        />
      );
    },
  },
  {
    id: "ventilation",
    compartment: "5",
    label: "Ventilation",
    stages: [...VENT_STAGES, ...DIAGNOSIS_STAGES],
    render: (stage) => {
      const previewDiagnosis = diagnosisFixture("nilan", stage);
      return (
        <VentilationScreen
          initialStage={previewDiagnosis ? "connecting" : (stage as VentStage)}
          busy={false}
          error=""
          onComplete={noop}
          skipping={false}
          onSkip={noop}
          previewDiagnosis={previewDiagnosis}
          preview
        />
      );
    },
  },
  {
    id: "offboarding",
    compartment: "—",
    label: "Offboarding / handoff",
    stages: ["default", "with-skips"],
    render: (stage) => (
      <OffboardingScreen
        onClose={noop}
        skippedSteps={stage === "with-skips" ? [MODULE_NAMES.floor_heating!] : []}
      />
    ),
  },
  {
    id: "kit-sheet",
    compartment: "—",
    // Not a screen: the printed A4 sheet that goes in the box. It lives here
    // because the states worth reviewing are the ones that depend on a household
    // record being incomplete, and reproducing those in the admin UI means
    // editing a real household.
    label: "Kit sheet (print)",
    stages: KIT_SHEET_STAGES,
    render: (stage) => <KitSheetPreview stage={stage} />,
  },
];

export function PreviewPage() {
  const [params, setParams] = useSearchParams();

  const entry = SCREENS.find((s) => s.id === params.get("screen")) ?? SCREENS[0]!;
  const rawStage = params.get("stage") ?? "";
  const stage = entry.stages.includes(rawStage) ? rawStage : entry.stages[0]!;
  const stageIndex = entry.stages.indexOf(stage);
  const showChrome = params.get("chrome") !== "0";

  const go = (screenId: string, stageId: string) =>
    setParams({ screen: screenId, stage: stageId }, { replace: true });

  // Walk every stage of every screen as one flat list, so prev/next crosses
  // screen boundaries — the whole flow end to end without going back to a menu.
  const flat = SCREENS.flatMap((s) => s.stages.map((st) => ({ screen: s.id, stage: st })));
  const flatIndex = flat.findIndex((f) => f.screen === entry.id && f.stage === stage);
  const step = (delta: number) => {
    const next = flat[flatIndex + delta];
    if (next) {
      go(next.screen, next.stage);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {showChrome && (
        <div className="sticky top-0 z-50 flex flex-wrap items-center gap-2 bg-neutral-900 px-3 py-2 text-[13px] text-white">
          <span className="font-semibold tracking-wide text-neutral-400">SCREEN GALLERY</span>
          <select
            aria-label="Screen"
            value={entry.id}
            onChange={(event) => {
              const next = SCREENS.find((s) => s.id === event.target.value)!;
              go(next.id, next.stages[0]!);
            }}
            className="rounded bg-neutral-700 px-2 py-1"
          >
            {SCREENS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.compartment === "—" ? s.label : `${s.compartment}. ${s.label}`}
              </option>
            ))}
          </select>
          <select
            aria-label="Stage"
            value={stage}
            onChange={(event) => go(entry.id, event.target.value)}
            className="rounded bg-neutral-700 px-2 py-1"
          >
            {entry.stages.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
          <span className="text-neutral-400">
            stage {stageIndex + 1}/{entry.stages.length} · screen {flatIndex + 1}/{flat.length}
          </span>
          <span className="ml-auto flex gap-1">
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={flatIndex <= 0}
              className="rounded bg-neutral-700 px-3 py-1 disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={flatIndex >= flat.length - 1}
              className="rounded bg-neutral-700 px-3 py-1 disabled:opacity-40"
            >
              Next →
            </button>
          </span>
        </div>
      )}
      {/*
        The key is load-bearing. Each module screen keeps its stage in
        useState(initialStage ?? …), and React preserves that state when the
        same component re-renders with a new prop. So changing the stage in the
        toolbar updated the URL and the prop, and the screen carried on showing
        the stage it mounted with. Keying on screen+stage forces a remount, so
        the new initialStage is actually read.
      */}
      <div key={`${entry.id}:${stage}`} className="mx-auto w-full max-w-[390px] bg-white">
        {entry.render(stage)}
      </div>
    </div>
  );
}
